import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import {
  ContactCreatePayload,
  ContactUpdatePayload,
  ILocation,
  IMainContact,
  LocationContact,
} from 'src/app/features/customers/models/customers.models';
import { CustomersService } from 'src/app/features/customers/services/customers.services';
import { CommonService } from 'src/app/shared/common.service';
import { AlertEnums } from 'src/app/shared/enums/shared.enums';
import { emailValidator, numericOnlyValidator } from 'src/app/shared/validators/fields-validators';

export interface AddContactDialogData {
  contacts_information: IMainContact[];
  currentContact?: IMainContact;
  /** Required unless `bufferOnly` is true. */
  locationId?: number;
  /** Return draft contact instead of POSTing when the parent location is not saved yet. */
  bufferOnly?: boolean;
  availableLocations?: ILocation[];
  showLocationSelector?: boolean;
}

export interface AddContactDialogResult {
  newContact?: LocationContact | (Omit<LocationContact, 'id'> & { id?: number });
  updatedContact?: LocationContact;
  /** Set (edit mode only) when the user asks to delete this contact; the parent confirms and deletes. */
  deleteContactId?: number;
  locationId?: number;
}

/** Create/edit contact dialog for location or contacts-tab flows. */
@Component({
  selector: 'app-add-contacts',
  templateUrl: './add-contacts.component.html',
  styleUrls: ['./add-contacts.component.scss']
})
export class AddContactsComponent implements OnInit {

  form!: FormGroup;
  isEditMode = false;
  initialIsUserState = false;
  isSubmitting = false;
  selectedLocationId: number = 0;
  private contactId: number | undefined;

  constructor(
    public dialogRef: MatDialogRef<AddContactsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AddContactDialogData,
    private fb: FormBuilder,
    private customersService: CustomersService,
    private commonService: CommonService,
    private translateService: TranslateService,
    public featureFlagService: FeatureFlagService,
  ) {}

  ngOnInit(): void {
    this.selectedLocationId = this.data.locationId ?? 0;
    if (this.data.currentContact) {
      this.isEditMode = true;
      this.contactId = this.data.currentContact.id;
      this.initForm(this.data.currentContact);
    } else {
      this.initForm();
    }
  }

  onLocationChange(event: any): void {
    this.selectedLocationId = +event.target.value;
  }

  private initForm(contact?: IMainContact): void {
    const isUserValue = contact?.is_user ?? false;
    this.initialIsUserState = isUserValue;

    // Main contact toggle stays disabled; portal revocation uses admin flow.
    this.form = this.fb.group({
      contact_fname: new FormControl(contact?.contact_fname ?? '', Validators.required),
      contact_lname: new FormControl(contact?.contact_lname ?? ''),
      email: new FormControl(contact?.email ?? '', [Validators.required, emailValidator()]),
      phone: new FormControl(contact?.phone ?? '', numericOnlyValidator()),
      signature: new FormControl(contact?.signature ?? ''),
      receive_emails: new FormControl(contact?.receive_emails ?? true),
      receive_sms: new FormControl(contact?.receive_sms ?? false),
      is_user: new FormControl({
        value: isUserValue,
        disabled: isUserValue,
      }),
      lang: new FormControl(contact?.lang ?? 'it_IT', Validators.required),
      create_date: new FormControl(contact?.create_date ?? new Date().toISOString()),
    });
  }

  proceed(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;

    if (this.isEditMode && this.contactId) {
      const targetLocationId = this.selectedLocationId || this.data.locationId;
      if (!targetLocationId) {
        this.isSubmitting = false;
        this.commonService.showToast(
          this.translateService.instant(`alerts.${AlertEnums.contactUpdateError}`),
          'ERROR'
        );
        return;
      }
      const payload = this.buildUpdatePayload();
      this.customersService.updateLocationContact(targetLocationId, this.contactId, payload).subscribe({
        next: (res) => {
          const updated = res.object ?? res.data?.[0];
          this.commonService.showToast(
            this.translateService.instant(`alerts.${AlertEnums.contactUpdateSuccess}`),
            'SUCCESS'
          );
          this.dialogRef.close({ updatedContact: updated, locationId: this.selectedLocationId } as AddContactDialogResult);
        },
        error: () => {
          this.isSubmitting = false;
        },
      });
    } else {
      const payload = this.buildCreatePayload();

      if (this.data.bufferOnly) {
        this.dialogRef.close({ newContact: payload } as AddContactDialogResult);
        return;
      }

      const targetLocationId = this.selectedLocationId || this.data.locationId;
      if (!targetLocationId) {
        this.isSubmitting = false;
        this.commonService.showToast(
          this.translateService.instant(`alerts.${AlertEnums.contactAddError}`),
          'ERROR'
        );
        return;
      }
      this.customersService.createLocationContact(targetLocationId, payload).subscribe({
        next: (res) => {
          const created = res.object ?? res.data?.[0];
          this.commonService.showToast(
            this.translateService.instant(`alerts.${AlertEnums.contactAddSuccess}`),
            'SUCCESS'
          );
          this.dialogRef.close({ newContact: created, locationId: this.selectedLocationId } as AddContactDialogResult);
        },
        error: () => {
          this.isSubmitting = false;
        },
      });
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  /**
   * Edit mode only: signal the parent to delete this contact. The parent owns the
   * confirmation prompt and the delete request (NgRx), plus the local list update,
   * so the dialog just closes with the intent rather than calling the API itself.
   */
  requestDelete(): void {
    if (!this.isEditMode || this.contactId == null) return;
    const targetLocationId = this.selectedLocationId || this.data.locationId;
    this.dialogRef.close({ deleteContactId: this.contactId, locationId: targetLocationId } as AddContactDialogResult);
  }

  private buildCreatePayload(): ContactCreatePayload {
    const raw = this.form.getRawValue();
    const payload: Partial<ContactCreatePayload> = {
      contact_fname: raw.contact_fname?.trim(),
      email: raw.email?.toLowerCase().trim(),
      receive_emails: raw.receive_emails,
      receive_sms: raw.receive_sms,
      is_user: raw.is_user,
      lang: raw.lang,
    };
    if (raw.contact_lname?.trim()) payload.contact_lname = raw.contact_lname.trim();
    if (raw.phone?.trim())         payload.phone = raw.phone.trim();
    if (raw.signature?.trim())     payload.signature = raw.signature.trim();
    return payload as ContactCreatePayload;
  }

  /** Send `null` to clear optional strings; omitted keys are left unchanged. */
  private buildUpdatePayload(): ContactUpdatePayload {
    const raw = this.form.getRawValue();
    const payload: ContactUpdatePayload = {
      contact_fname: raw.contact_fname?.trim(),
      email: raw.email?.toLowerCase().trim(),
      receive_emails: raw.receive_emails,
      receive_sms: raw.receive_sms,
      is_user: raw.is_user,
      lang: raw.lang,
    };
    if (raw.contact_lname?.trim()) payload.contact_lname = raw.contact_lname.trim();
    else if (raw.contact_lname !== undefined) payload.contact_lname = null;
    if (raw.phone?.trim())         payload.phone = raw.phone.trim();
    else if (raw.phone !== undefined) payload.phone = null;
    if (raw.signature?.trim())     payload.signature = raw.signature.trim();
    else if (raw.signature !== undefined) payload.signature = null;
    return payload;
  }
}
